import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api/client';
import { useAuth } from '../state/auth';
import { Bandeau, Carte, Champ, Modale, Squelette, Vide } from '../components/ui';
import { useMobile } from '../lib/ecran';
import { centimes, dateFr, heureFr, montant } from '../lib/format';
import type { Mouvement, Tiroir } from '../api/types';
import { T } from '../i18n';

interface Tiroirs { tiroirs: Tiroir[]; totalReferenceMinor: number; deviseReference: string }

/**
 * Le compte en banque.
 *
 * La caisse et la banque sont deux poches differentes : on ne paie pas un
 * garagiste en especes avec de l'argent qui dort sur le compte. Deplacer de
 * l'argent entre les deux cree DEUX mouvements apparies — une sortie ici, une
 * entree la-bas — parce qu'un seul ferait apparaitre ou disparaitre de l'argent,
 * et c'est le genre d'ecart qu'on ne retrouve jamais trois mois plus tard.
 */
export default function Banque() {
  const { peut } = useAuth();
  const mobile = useMobile();
  const [t, setT] = useState<Tiroirs | null>(null);
  const [mvts, setMvts] = useState<Mouvement[] | null>(null);
  const [ecran, setEcran] = useState<'' | 'transfert' | 'frais'>('');
  const [msg, setMsg] = useState('');

  const recharger = useCallback(() => {
    api.get<Tiroirs>('/caisse/banque/tiroirs').then(setT).catch(() => {});
    api.get<Mouvement[]>('/caisse/banque/mouvements').then(setMvts).catch(() => setMvts([]));
  }, []);
  useEffect(recharger, [recharger]);

  if (!t || mvts === null) return <Squelette lignes={6} />;
  const tx = T.banque;

  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>{tx.titre}</h1>
          <p>{tx.sousTitre}</p>
        </div>
        {peut('modifier.finance') && (
          <div className="actions">
            <button className="btn btn-p" onClick={() => setEcran('transfert')}>{tx.versementRetrait}</button>
            <button className="btn" onClick={() => setEcran('frais')}>{tx.fraisBancaires}</button>
          </div>
        )}
      </div>

      {msg && <div style={{ marginBottom: 14 }}><Bandeau type="ok">{msg}</Bandeau></div>}

      <div className="tiroirs" style={{ marginBottom: 16 }}>
        {t.tiroirs.map((x) => (
          <div key={x.devise} className="carte carte-p">
            <div className="micro">{x.libelle} · {x.devise}</div>
            <div className="v montant">{montant(x.soldeMinor, x.devise, x.decimales)}</div>
            {x.devise !== t.deviseReference && (
              <div className="mini sourdine">≈ {montant(x.contrevaleurMinor)} </div>
            )}
          </div>
        ))}
        <div className="carte carte-p signature-kpi">
          <div className="micro">{tx.totalBanque}</div>
          <div className="v">{montant(t.totalReferenceMinor)}</div>
        </div>
      </div>

      <Carte pad={false} titre={tx.mouvements}>
        {mvts.length === 0 ? (
          <div className="carte-p">
            <Vide titre={tx.videTitre}
                  action={peut('modifier.finance')
                    ? <button className="btn btn-p btn-l" onClick={() => setEcran('transfert')}>{tx.videAction}</button>
                    : undefined}>
              {tx.videTexte}
            </Vide>
          </div>
        ) : mobile ? (
          <div className="cartes">
            {mvts.map((m) => (
              <div key={m._id} className="carte-ligne">
                <div className="cl-tete">
                  <span className="cl-titre">{m.libelle}</span>
                  <span className="montant">{montant(m.contrevaleurMinor)}</span>
                </div>
                <div className="cl-sous">{dateFr(m.date)} · {m.creePar?.nom ?? '—'}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="enveloppe-tbl">
            <table className="tbl">
              <thead><tr><th>{tx.colonnes.date}</th><th>{tx.colonnes.libelle}</th><th>{tx.colonnes.reference}</th><th className="droite">{tx.colonnes.montant}</th><th>{tx.colonnes.par}</th></tr></thead>
              <tbody>
                {mvts.map((m) => (
                  <tr key={m._id}>
                    <td className="mini sourdine">{dateFr(m.date)} <span className="num">{heureFr(m.date)}</span></td>
                    <td>{m.libelle}</td>
                    <td className="mini sourdine">{m.pieceJustificative ?? '—'}</td>
                    <td className="droite montant"
                        style={{ color: m.type === 'transfert_entree' ? 'var(--succes)' : 'var(--danger)' }}>
                      {m.type === 'transfert_entree' ? '+' : '−'}{montant(m.contrevaleurMinor).replace('−', '')}
                    </td>
                    <td className="mini sourdine">{m.creePar?.nom ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Carte>

      <p className="mini sourdine" style={{ marginTop: 12 }}>
        {tx.note}
      </p>

      {ecran === 'transfert' && (
        <Transfert devise={t.deviseReference}
                   onFermer={() => setEcran('')}
                   onFini={(m) => { setEcran(''); setMsg(m); recharger(); }} />
      )}
      {ecran === 'frais' && (
        <Frais devise={t.deviseReference}
               onFermer={() => setEcran('')}
               onFini={(m) => { setEcran(''); setMsg(m); recharger(); }} />
      )}
    </>
  );
}

function Transfert({ devise, onFini, onFermer }: { devise: string; onFini: (m: string) => void; onFermer: () => void }) {
  const tx = T.banque.transfert;
  const [sens, setSens] = useState<'vers_banque' | 'vers_caisse'>('vers_banque');
  const [mt, setMt] = useState('');
  const [libelle, setLibelle] = useState(tx.libelleDepot);
  const [reference, setReference] = useState('');
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);

  async function envoyer() {
    setErreur(''); setEnvoi(true);
    try {
      await api.post('/caisse/banque/transfert', {
        sens, devise, montantMinor: centimes(Number(mt)), libelle, reference: reference || undefined,
      });
      onFini(sens === 'vers_banque'
        ? tx.depose(montant(centimes(Number(mt))))
        : tx.retire(montant(centimes(Number(mt)))));
    } catch (e) { setErreur(e instanceof ApiError ? e.erreur : tx.impossible); }
    finally { setEnvoi(false); }
  }

  return (
    <Modale titre={T.banque.versementRetrait} onFermer={onFermer}>
      <Champ label={tx.sens}>
        <select value={sens} onChange={(e) => {
          const v = e.target.value as typeof sens;
          setSens(v);
          setLibelle(v === 'vers_banque' ? tx.libelleDepot : tx.libelleRetrait);
        }}>
          <option value="vers_banque">{tx.versBanque}</option>
          <option value="vers_caisse">{tx.versCaisse}</option>
        </select>
      </Champ>
      <div className="grille g2" style={{ marginTop: 12 }}>
        <Champ label={T.banque.transfert.montant(devise)}>
          <input type="number" inputMode="decimal" value={mt} onChange={(e) => setMt(e.target.value)} autoFocus />
        </Champ>
        <Champ label={tx.reference} aide={tx.referenceAide}>
          <input value={reference} onChange={(e) => setReference(e.target.value)} />
        </Champ>
      </div>
      <div style={{ marginTop: 12 }}>
        <Champ label={T.banque.transfert.libelle}><input value={libelle} onChange={(e) => setLibelle(e.target.value)} /></Champ>
      </div>
      <div style={{ marginTop: 12 }}>
        <Bandeau type="info" titre={tx.deuxLignes}>
          {tx.explication(sens === 'vers_banque')}
        </Bandeau>
      </div>
      {erreur && <div style={{ marginTop: 10 }}><Bandeau type="ko">{erreur}</Bandeau></div>}
      <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
        <button className="btn" onClick={onFermer}>{T.commun.actions.annuler}</button>
        <button className="btn btn-p" disabled={!(Number(mt) > 0) || libelle.length < 2 || envoi}
                onClick={() => void envoyer()}>{T.commun.actions.enregistrer}</button>
      </div>
    </Modale>
  );
}

function Frais({ devise, onFini, onFermer }: { devise: string; onFini: (m: string) => void; onFermer: () => void }) {
  const tx = T.banque.frais;
  const [mt, setMt] = useState('');
  const [libelle, setLibelle] = useState(tx.libelleDefaut);
  const [erreur, setErreur] = useState('');

  async function envoyer() {
    setErreur('');
    try {
      await api.post('/caisse/banque/frais', { devise, montantMinor: centimes(Number(mt)), libelle });
      onFini(tx.enregistres(montant(centimes(Number(mt)))));
    } catch (e) { setErreur(e instanceof ApiError ? e.erreur : tx.impossible); }
  }

  return (
    <Modale titre={T.banque.fraisBancaires} onFermer={onFermer}>
      <p className="mini sourdine" style={{ marginTop: -4, marginBottom: 12 }}>
        {tx.explication}
      </p>
      <Champ label={T.banque.transfert.montant(devise)}>
        <input type="number" inputMode="decimal" value={mt} onChange={(e) => setMt(e.target.value)} autoFocus />
      </Champ>
      <div style={{ marginTop: 12 }}>
        <Champ label={T.banque.transfert.libelle}><input value={libelle} onChange={(e) => setLibelle(e.target.value)} /></Champ>
      </div>
      {erreur && <div style={{ marginTop: 10 }}><Bandeau type="ko">{erreur}</Bandeau></div>}
      <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
        <button className="btn" onClick={onFermer}>{T.commun.actions.annuler}</button>
        <button className="btn btn-p" disabled={!(Number(mt) > 0)} onClick={() => void envoyer()}>{T.commun.actions.enregistrer}</button>
      </div>
    </Modale>
  );
}
